import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NotificationService } from 'src/app/Service/notification.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { OtherService } from 'src/app/Service/other.service';
import * as XLSX from 'xlsx';
import {
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
} from '@angular/animations';
import axios from 'src/app/Service/axios';
import { Subject, takeUntil, timer } from 'rxjs';
import { MenuItem, MENUITEMS } from '../fleetdiag-live-parameters/fleetdiag-live-parameters.component';

export interface IChart {
  iOBD2_LatestState: number;
  iOBD2_Code: number;
  strUnitID: string;
  strValue: string;
  dtDateTime: string;
  strComment: string;
  MinInterval: number;
  CriticalValue: any;
  strEnglishName: string;
  strUnit: string;
  fLat: number;
  fLon: number;
  tiPrecision: number;
  fMin: number;
  fMax: number;
  tiChartType: number;
  bContinuousUpdate: boolean;
  tiGroup: number;
  strPID: string;
  strDateTime: string;
  chartData: Array<any>;
}

@Component({
  selector: 'app-fleetdiag-all-items',
  templateUrl: './fleetdiag-all-items.component.html',
  styleUrls: [
    '../../fleetdiag-dashboard-pro.component.scss',
    './fleetdiag-all-items.component.css',
  ],
  animations: [
    trigger('listAnimation', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0 }),
            stagger(150, [
              animate(
                '500ms cubic-bezier(0.35, 0, 0.25, 1)',
                style({ opacity: 1 }),
              ),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ],
})
export class FleetdiagAllItemsComponent implements OnInit {
  menuItems: MenuItem[] = MENUITEMS;
  title!: string;
  charts: IChart[] = [];

  private destroy$ = new Subject<void>();

  searchForm: FormGroup = this.FormBuilder.group({
    iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
    strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    tiGroup: new FormControl(''),
    strUnitID: new FormControl(''),
    iFleetDiag_SystemUser: new FormControl(''),
    source: new FormControl(''),
  });

  constructor(
    private _tooltip: NotificationService,
    private FormBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private gurdService: GurdServiceService,
    private _otherService: OtherService,
  ) { }

  ngOnInit(): void {
    try {
      const { id, slug } = this.route.snapshot.params;

      const isFleetUser = id.length < 15 && id.length > 3;
      const controlName = isFleetUser ? 'iFleetDiag_SystemUser' : 'strUnitID';

      this.searchForm.patchValue({
        [controlName]: id,
        tiGroup: slug,
        source: isFleetUser ? 'users' : 'car-info'
      });

      this.title = this.menuItems.find(m => m.routerLink === slug)?.title ?? '';

      this._otherService.Showspinner();
      timer(0, 3000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.fetchData());

    } catch {
      this._tooltip.showError('خطا در تشخیص شناسه یکتا..');
    }
  }

  ngOnDestroy() {
    this.stopTimer();
  }

  stopTimer() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async fetchData() {
    const model = this._otherService.stringNumberToNumber(this.searchForm.value);

    const endpoint =
      this.searchForm.value.source === 'users'
        ? 'ELM_OBD2_Code_ListAndLastData4FleetDiag'
        : 'ELM_OBD2_Code_ListAndLastData';

    const response = JSON.parse(
      (await axios.post(endpoint, model)).data.toString()
    );


    this._otherService.nonespinner();

    if (response.ISSessionExpired == true) {
      this._tooltip.showError('نشست کاربری شما منقضی شده است');
      this._otherService.removeLocalStorage();
      this.router.navigate(['login'], { queryParams: { error: 'ELM-OBD2' } });
      return;
    }

    if (response.iResult == 0) {
      return this._tooltip.showError(response.strError);
    }

    this.charts = response.map((newItem: { strPID: string }) => {
      const oldItem = this.charts.find((x) => x.strPID === newItem.strPID);

      if (oldItem?.chartData) return oldItem;

      return newItem;
    });
  }

  returnStatusClass(item: any): string {
    const percentDiff =
      (Math.abs(item.strValue - item.CriticalValue) / item.CriticalValue) * 100;

    if (percentDiff <= 5) {
      return 'text-danger';
    }

    if (percentDiff <= 10) {
      return 'text-warning';
    } else {
      return 'text-success';
    }
  }

  trackByPid(index: number, item: IChart): string {
    return item.strPID;
  }

  async toggleChart(item: IChart) {
    if (item.chartData?.length) {
      this.charts = this.charts.map((x) =>
        x.strPID === item.strPID ? { ...x, chartData: [] } : x,
      );
    } else {
      this._otherService.Showspinner();

      const model = this._otherService.stringNumberToNumber({
        ...this.searchForm.value,
        strPID: item.strPID,
      });

      const endpoint =
        this.searchForm.value.source === 'users'
          ? 'FleetDiag_PID_Chart4FleetDiag'
          : 'FleetDiag_PID_Chart';

      const response = JSON.parse(
        (await axios.post(endpoint, model)).data.toString()
      );


      this._otherService.nonespinner();

      if (response.ISSessionExpired == true) {
        this._tooltip.showError('نشست کاربری شما منقضی شده است');
        this._otherService.removeLocalStorage();
        this.router.navigate(['login'], {
          queryParams: { error: 'PID_Chart' },
        });
        return;
      }

      if (response.iResult == 0) {
        return this._tooltip.showError(response.strError);
      }

      this.charts = this.charts.map((x) =>
        x.strPID === item.strPID ? { ...x, chartData: response } : x,
      );
    }
  }

  exportExcel(item: any): void {
    const data = item?.chartData || [];

    const sheetData = [
      ['توضیحات', 'تاریخ', 'مقدار'],
      ...data.map((row: any) => [
        row.strComment,
        row.X,
        row.Y
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    worksheet['!cols'] = [
      { wch: 40 },
      { wch: 25 },
      { wch: 15 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'گزارش');

    XLSX.writeFile(workbook, `${item.strComment}.xlsx`);
  }

  openFullScreenChart(item: any) {
    const urlTree = this.router.createUrlTree([
      'fleetdiag',
      item.strUnitID,
      'workspace',
      'chart',
      item.strPID,
      item.strComment.trim().replace(/\s+/g, '-')
    ]);

    const serializedUrl = this.router.serializeUrl(urlTree)
    window.open(serializedUrl, '_blank');
  }
}
